import React from 'react';
import { Calendar, DollarSign, TrendingUp } from 'lucide-react';
import { PackageUI } from '@/api/type';

export type PlanType = 'short_term' | 'fixed' | 'staking' | 'long_term';
export type PlanStatus = 'open' | 'closed' | 'expired';


interface PlanCardProps {
  plan: PackageUI;
  onViewDetail: (plan: PackageUI) => void;
}

const typeLabels: Record<PlanType, string> = {
  short_term: 'Ngắn hạn',
  fixed: 'Cố định',
  staking: 'Staking',
  long_term: 'Dài hạn',
};

const typeStyles: Record<PlanType, string> = {
  short_term: '!bg-blue-200 !text-blue-700',
  fixed: '!bg-orange-100 !text-orange-800',
  staking: '!bg-lime-200 !text-lime-700',
  long_term: '!bg-red-200 !text-red-700',
};

const statusLabels: Record<PlanStatus, string> = {
  open: 'Đang mở',
  closed: 'Đã đóng',
  expired: 'Hết hạn',
};

const statusStyles: Record<PlanStatus, string> = {
  open: '!bg-green-500',
  closed: '!bg-gray-500',
  expired: '!bg-red-500',
};

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('vi-VN').format(value) + 'đ';
};

export const PlanCard: React.FC<PlanCardProps> = ({ plan, onViewDetail }) => {
  const isDisabled = plan.status !== 'open';

  return (
    <div className="!bg-white dark:!bg-main-dark !rounded-md overflow-hidden !shadow-sm hover:!shadow-md !transition-shadow">
      <div className="relative h-40 sm:h-48">
        <img
          src={plan.image || '/images/plan-default.jpg'}
          alt={plan.name}
          className="w-full h-full object-cover"
        />
        <span className={`absolute top-3 left-3 !px-3 !py-1 !rounded-md !text-xs !font-medium ${typeStyles[plan.type as PlanType]}`}>
          {typeLabels[plan.type as PlanType]}
        </span>
        <span className={`absolute top-3 right-3 !px-3 !py-1 !rounded-md !text-xs !font-medium !text-white flex items-center gap-1.5 ${statusStyles[plan.status as PlanStatus]}`}>
          <span className="w-1.5 h-1.5 rounded-full !bg-white" />
          {statusLabels[plan.status as PlanStatus]}
        </span>
      </div>

      <div className="p-4 sm:p-5">
        <h3 className="!text-base sm:!text-lg !font-bold !text-gray-900 dark:!text-white mb-2">
          {plan.name}
        </h3>
        <p className="!text-xs sm:!text-sm !text-gray-500 dark:!text-dark-text-secondary mb-4 line-clamp-2">
          {plan.description}
        </p>

        <div className="space-y-2.5">
          <div className="flex items-center gap-2 !text-sm">
            <DollarSign className="w-4 h-4 !text-gray-400 dark:!text-dark-text-muted" />
            <span className="!text-gray-800 dark:!text-dark-text-secondary">Tổng đầu tư:</span>
            <span className="!font-semibold !text-gray-900 dark:!text-white">
              {formatCurrency(plan.minInvestment)}
            </span>
          </div>
          <div className="flex items-center gap-2 !text-sm">
            <TrendingUp className="w-4 h-4 !text-gray-400 dark:!text-dark-text-muted" />
            <span className="!text-gray-800 dark:!text-dark-text-secondary">Lợi nhuận:</span>
            <span className="text-status-success">
              {
                plan.profitRate > 0
                  ? `${plan.profitRate}%/${plan.profitPeriod === 'month' ? 'tháng' : 'năm'}`
                  : 'Theo doanh thu'
              }
            </span>
          </div>
          <div className="flex items-center gap-2 !text-sm">
            <Calendar className="w-4 h-4 !text-gray-400 dark:!text-dark-text-muted" />
            <span className="!text-gray-800 dark:!text-dark-text-secondary">Thời hạn:</span>
            <span className=" !text-gray-900 dark:!text-white">
              {plan.duration === 0 ? 'Đến khi hủy' : plan.duration + ' tháng'} 
            </span>
          </div>
        </div>

        <button
          onClick={() => onViewDetail(plan)}
          disabled={isDisabled}
          className={`w-full !mt-5 !py-2.5 !rounded-md !text-sm !font-medium !transition-colors !border-none ${
            isDisabled
              ? '!bg-gray-200 dark:!bg-dark-border !text-gray-400 dark:!text-dark-text-muted cursor-not-allowed'
              : '!bg-primary hover:!bg-primary-dark !text-white'
          }`}
        >
          {isDisabled ? 'Không khả dụng' : 'Xem chi tiết'}
        </button>
      </div>
    </div>
  );
};
